import type { ReactElement } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Info, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAddTicketReply, useTicket } from "@/hooks/use-tickets";
import { TicketStatus, ticketErrorMessage, type TicketAttachment } from "@/lib/api/tickets";
import { ApiError } from "@/lib/api/client";
import { TicketStatusBadge } from "@/components/tickets/ticket-status-badge";
import { TicketThread } from "@/components/tickets/ticket-thread";
import { TicketComposer } from "@/components/tickets/ticket-composer";

export const Route = createFileRoute("/_authenticated/support_/$ticketId")({
  component: StudentTicketDetailPage,
});

function StudentTicketDetailPage(): ReactElement {
  const { isStudent } = useAuth();

  if (!isStudent) {
    return (
      <AccessDenied message="The support portal is for students. Staff manage tickets from the Tickets inbox." />
    );
  }

  return <StudentTicketDetailContent />;
}

function StudentTicketDetailContent(): ReactElement {
  const { ticketId } = Route.useParams();
  const { data: ticket, isLoading, isError, error, refetch } = useTicket(ticketId);
  const addReplyMutation = useAddTicketReply(ticketId);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
        <Skeleton className="h-6 w-32" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-5 w-1/3" />
        </div>
        <div className="space-y-4 pt-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !ticket) {
    if (error instanceof ApiError && (error.status === 403 || error.status === 401)) {
      return (
        <AccessDenied message="You do not have permission to view this ticket or it belongs to another student." />
      );
    }

    const isNotFound = error instanceof ApiError && error.status === 404;

    return (
      <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
        <Link
          to="/support"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          <span>Back to tickets</span>
        </Link>
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-12 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">
            {isNotFound ? "Ticket not found" : "Could not load ticket"}
          </p>
          <p className="text-xs text-muted-foreground">
            {isNotFound
              ? "The requested support ticket does not exist or may have been removed."
              : ticketErrorMessage(error, "An unexpected error occurred while fetching the ticket.")}
          </p>
          {!isNotFound && (
            <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  const createdDate = new Date(ticket.createdAt);
  const isCreatedValid = !Number.isNaN(createdDate.getTime());
  const relativeFiled = isCreatedValid
    ? formatDistanceToNow(createdDate, { addSuffix: true })
    : "recently";

  const isClosed = ticket.status === TicketStatus.Closed;
  const isResolved = ticket.status === TicketStatus.Resolved;

  function handleReplySubmit(input: {
    body: string;
    isInternalNote: boolean;
    attachment: TicketAttachment | null;
  }) {
    addReplyMutation.mutate(
      {
        body: input.body,
        isInternalNote: false,
        attachment: input.attachment,
      },
      {
        onSuccess: () => {
          toast.success("Reply posted.");
        },
        onError: (mutationError) => {
          toast.error(ticketErrorMessage(mutationError, "Failed to post reply."));
        },
      },
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
      <Link
        to="/support"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        <span>Back to tickets</span>
      </Link>

      <header className="space-y-2 border-b pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
            {ticket.subject}
          </h1>
          <div className="shrink-0">
            <TicketStatusBadge status={ticket.status} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">{ticket.topicTitle}</span>
          <span>•</span>
          <time dateTime={ticket.createdAt} title={createdDate.toLocaleString()}>
            Filed {relativeFiled}
          </time>
        </div>
      </header>

      <div className="space-y-6">
        <section aria-label="Conversation messages">
          <TicketThread ticket={ticket} showInternalNotes={false} />
        </section>

        {isResolved && (
          <div className="flex items-center gap-2 rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-900 dark:text-blue-200">
            <Info
              className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400"
              aria-hidden="true"
            />
            <span>
              This ticket is marked as resolved. Replying will automatically reopen it for further
              assistance.
            </span>
          </div>
        )}

        <section aria-label="Reply to ticket">
          <TicketComposer
            onSubmit={handleReplySubmit}
            isPending={addReplyMutation.isPending}
            allowInternalNote={false}
            disabled={isClosed}
            disabledReason="This ticket is closed. Reply is no longer available."
          />
        </section>
      </div>
    </div>
  );
}
